import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { t } from 'ttag';
import { useApolloClient } from '@apollo/react-hooks';

import Avatar from '../Avatar';
import { MyClubCApi } from '../../api';
import { formatRank } from '../../utils/helpers';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 12,
    overflow: 'hidden',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  closeButton: {
    marginTop: -4,
    marginRight: -8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2.5),
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: 16,
    flexShrink: 0,
  },
  headerMeta: {
    minWidth: 0,
    flex: 1,
    marginLeft: theme.spacing(2),
  },
  name: {
    fontWeight: 700,
    lineHeight: 1.25,
    wordBreak: 'break-word',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    '& > *': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 42%) 1fr',
    columnGap: theme.spacing(1.5),
    alignItems: 'start',
    marginBottom: theme.spacing(1.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
  },
  value: {
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 220,
  },
  empty: {
    color: theme.palette.text.disabled,
    fontWeight: 500,
  },
}));

const displayValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const text = String(value).trim();
  return text || null;
};

const InfoRow = ({ label, value, classes }) => {
  const shown = displayValue(value);
  return (
    <div className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={shown ? classes.value : `${classes.value} ${classes.empty}`}>
        {shown || '—'}
      </Typography>
    </div>
  );
};

const AthleteViewDialog = ({ objId, open = true, onClose }) => {
  const classes = useStyles();
  const client = useApolloClient();
  const api = React.useMemo(() => new MyClubCApi(client), [client]);

  const [loading, setLoading] = React.useState(false);
  const [athlete, setAthlete] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!objId || !open) {
        return;
      }
      setLoading(true);
      try {
        const res = await api.getAthlete(objId);
        if (!cancelled) {
          setAthlete(res);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load athlete', error);
        if (!cancelled) {
          setAthlete(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, objId, open]);

  const rankLabel = athlete ? formatRank(athlete.rank) : '';
  const qualification = athlete ? displayValue(athlete.qualification) : null;
  const showRankChip = Boolean(rankLabel && rankLabel !== 'Відсутні');

  return (
    <Dialog
      open={open && Boolean(objId)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography>
        <div className={classes.titleWrap}>
          <Typography variant="h6" className={classes.name}>
            {athlete?.fullName || t`Картка спортсмена`}
          </Typography>
          {onClose ? (
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={onClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {loading || !athlete ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <Avatar
                src={athlete.photo && athlete.photo.url}
                size="large"
                variant="rounded"
                className={classes.avatar}
              />
              <div className={classes.headerMeta}>
                <Typography variant="subtitle1" className={classes.name}>
                  {athlete.fullName}
                </Typography>
                {(showRankChip || qualification) && (
                  <div className={classes.chips}>
                    {showRankChip ? (
                      <Chip size="small" color="primary" label={rankLabel} />
                    ) : null}
                    {qualification ? (
                      <Chip size="small" variant="outlined" label={qualification} />
                    ) : null}
                  </div>
                )}
                {athlete.club && athlete.club.shortName ? (
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      {athlete.club.shortName}
                    </Typography>
                  </Box>
                ) : null}
              </div>
            </div>

            <Divider />

            <div className={classes.rows}>
              <InfoRow
                label={t`Дата народження`}
                value={athlete.birthday}
                classes={classes}
              />
              <InfoRow label={t`Email`} value={athlete.email} classes={classes} />
              <InfoRow label={t`Телефон`} value={athlete.phone} classes={classes} />
              <InfoRow label={t`Кю/Дан`} value={rankLabel} classes={classes} />
              <InfoRow
                label={t`Спортивна кваліфікація`}
                value={athlete.qualification}
                classes={classes}
              />
              <InfoRow label={t`WKF-ID`} value={athlete.leagueId} classes={classes} />
            </div>
          </>
        )}
      </DialogContent>

    </Dialog>
  );
};

export default AthleteViewDialog;
